import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../utils/supabaseClient";
import useRequireAdmin from "../../hooks/useRequireAdmin";
import AdminNav from "../../components/AdminNav";
import DeliverySettingsForm from "../../components/DeliverySettingsForm";

const stockOf = (p) => Number(p.stock ?? 0);
const thresholdOf = (p) => Number(p.low_stock_threshold ?? 5);

function StockBadge({ product }) {
  const stock = stockOf(product);
  if (stock <= 0) return <span className="ui-badge ui-badge--out">Out of stock</span>;
  if (stock <= thresholdOf(product)) {
    return <span className="ui-badge ui-badge--low">Low: {stock}</span>;
  }
  return <span className="ui-badge ui-badge--ok">{stock}</span>;
}

export default function AdminDashboard() {
  const checking = useRequireAdmin();

  const [products, setProducts] = useState([]);
  const [paidCount, setPaidCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (checking) return;
    let cancelled = false;

    async function load() {
      const [productsResult, paidResult] = await Promise.all([
        supabase
          .from("products")
          .select("id, name, price, stock, low_stock_threshold, category, image_url, created_at")
          .order("created_at", { ascending: false }),
        // Paid orders are the ones waiting to be sent out
        supabase
          .from("orders")
          .select("id", { count: "exact", head: true })
          .eq("status", "paid"),
      ]);

      if (cancelled) return;

      if (productsResult.error) {
        console.error(productsResult.error);
        setError("We couldn't load the products.");
      } else {
        setProducts(productsResult.data ?? []);
      }
      if (!paidResult.error) setPaidCount(paidResult.count ?? 0);

      setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [checking]);

  if (checking || loading) {
    return (
      <div className="adm">
        <p className="adm-muted">Loading dashboard...</p>
      </div>
    );
  }

  const outOfStock = products.filter((p) => stockOf(p) <= 0);
  const lowStock = products.filter((p) => stockOf(p) > 0 && stockOf(p) <= thresholdOf(p));

  const needsRestock = products
    .filter((p) => stockOf(p) <= thresholdOf(p))
    .sort((a, b) => stockOf(a) - stockOf(b))
    .slice(0, 8);

  const recent = products.slice(0, 5);

  return (
    <div className="adm">
      <div className="adm-header">
        <h2 className="adm-title">Admin dashboard</h2>
        <div className="adm-header-actions">
          <Link to="/admin/products/new" className="ui-btn ui-btn--primary">Add new product</Link>
          <Link to="/admin/products" className="ui-btn ui-btn--ghost">Manage products</Link>
          <Link to="/admin/orders" className="ui-btn ui-btn--ghost">View orders</Link>
        </div>
      </div>

      <AdminNav />

      {error && <p className="ui-alert ui-alert--error">{error}</p>}

      <div className="adm-strip">
        <Link
          to="/admin/orders"
          className={`adm-strip-item${paidCount > 0 ? " adm-strip-item--action" : ""}`}
        >
          <span className="adm-strip-value">{paidCount}</span>
          <span className="adm-strip-label">Paid orders to send out</span>
        </Link>
        <div className="adm-strip-item">
          <span className="adm-strip-value">{products.length}</span>
          <span className="adm-strip-label">Products</span>
        </div>
        <div className={`adm-strip-item${lowStock.length > 0 ? " adm-strip-item--warn" : ""}`}>
          <span className="adm-strip-value">{lowStock.length}</span>
          <span className="adm-strip-label">Running low</span>
        </div>
        <div className={`adm-strip-item${outOfStock.length > 0 ? " adm-strip-item--bad" : ""}`}>
          <span className="adm-strip-value">{outOfStock.length}</span>
          <span className="adm-strip-label">Out of stock</span>
        </div>
      </div>

      <div className="adm-grid-2">
        <section className="adm-card">
          <div className="adm-card-head">
            <h3>Needs restocking</h3>
            <Link to="/admin/products" className="ui-btn ui-btn--ghost ui-btn--sm">Update stock</Link>
          </div>
          {needsRestock.length === 0 ? (
            <p className="adm-empty">Every product has enough stock.</p>
          ) : (
            <div className="adm-table-wrap">
              <table className="adm-table" style={{ minWidth: 0 }}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {needsRestock.map((p) => (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td><StockBadge product={p} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="adm-card">
          <div className="adm-card-head">
            <h3>Recently added</h3>
          </div>
          {recent.length === 0 ? (
            <p className="adm-empty">No products yet. Add your first one.</p>
          ) : (
            <div className="adm-table-wrap">
              <table className="adm-table" style={{ minWidth: 0 }}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="adm-num">Price</th>
                    <th>Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="adm-cell-product">
                          {p.image_url ? (
                            <img className="adm-thumb" src={p.image_url} alt="" />
                          ) : (
                            <span className="adm-thumb adm-thumb--empty">{p.name?.charAt(0)}</span>
                          )}
                          <div>
                            <strong>{p.name}</strong>
                            <small>{p.category}</small>
                          </div>
                        </div>
                      </td>
                      <td className="adm-num">£{Number(p.price).toFixed(2)}</td>
                      <td><StockBadge product={p} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <div className="adm-section">
        <DeliverySettingsForm />
      </div>
    </div>
  );
}